import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { useEffect, useState } from 'react';

import { LanguageDataResponse, LanguagesDataModel } from '../../types';
import { fetchPaginatedCollectionRealtime } from '../utils';

export const useGetLanguagesFromFirestore = (
  page?: number,
  elements?: number,
  options?: UseQueryOptions<LanguagesDataModel, FirebaseError, LanguagesDataModel>,
): UseQueryResult<LanguagesDataModel, FirebaseError> => {
  const queryClient = useQueryClient();

  const subscriptionKey = ['languages', page ?? 1, elements ?? 20];
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<FirebaseError | null>(null);

  useEffect(() => {
    const unsubscribePromise = fetchPaginatedCollectionRealtime<LanguageDataResponse>(
      'languages',
      page ?? 1,
      elements ?? 20,
      'name',
      (data, totalPages) => {
        const mappedData = data.map((item) => ({
          id: item.id,
          code: item.code,
          name: item.name,
          createdAt: item.createdAt ?? null,
        }));
        setIsLoading(false);
        queryClient.setQueryData<LanguagesDataModel>(subscriptionKey, {
          data: mappedData,
          totalPages,
          isLoading: false,
          error: null,
        });
      },
      (e) => {
        setError(e as FirebaseError);
        setIsLoading(false);
      },
    );

    // eslint-disable-next-line consistent-return
    return () => {
      unsubscribePromise
        .then((unsubscribe) => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        })
        .catch((e) => {
          setError(e as FirebaseError);
          setIsLoading(false);
        });
    };
  }, [page, elements, queryClient]);

  return useQuery<LanguagesDataModel, FirebaseError, LanguagesDataModel>({
    queryKey: subscriptionKey,
    queryFn: async () => {
      const cachedData = queryClient.getQueryData<LanguagesDataModel>(subscriptionKey);
      if (cachedData) {
        return cachedData;
      }
      return { data: [], totalPages: 0, isLoading, error };
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};
